/**
 * PreFlightCheck — prove the camera, the microphone and the connection work
 * before a congregation, a class or an audience is watching.
 *
 * Runs on its own stream and releases it when finished, so the broadcast opens
 * the camera cleanly afterwards; two live streams at once upsets iOS.
 *
 * Deliberately shows what it measured — the picture size, the loudest sound it
 * heard, the round trip to the server — rather than a green tick, because
 * "sound: fine" is what people distrust after the first silent service.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Check, Loader2, Mic, Video, Wifi, X, AlertTriangle,
} from 'lucide-react';
import { describeCameraError, requestCameraStream } from '@/lib/cameraAccess';
import { CameraTrouble } from '@/components/live/CameraTrouble';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LISTEN_MS = 4000;
const QUIET = 0.015;          // below this, nothing useful reached the mic

const Row = ({ icon: Icon, label, state, detail }) => (
  <div className="flex items-start gap-3 py-3 border-b border-slate-800 last:border-0"
       data-testid={`preflight-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <Icon className="h-4 w-4 mt-0.5 text-slate-400 shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="text-sm text-slate-200">{label}</p>
      <p className="text-xs text-slate-400 mt-0.5">{detail}</p>
    </div>
    {state === 'running' && <Loader2 className="h-4 w-4 animate-spin text-slate-500" />}
    {state === 'ok' && <Check className="h-4 w-4 text-emerald-400" />}
    {state === 'warn' && <AlertTriangle className="h-4 w-4 text-amber-400" />}
    {state === 'fail' && <X className="h-4 w-4 text-rose-400" />}
  </div>
);

export const PreFlightCheck = ({ open, onOpenChange, onPassed }) => {
  const [camera, setCamera] = useState({ state: 'running', detail: 'Opening the camera…' });
  const [mic, setMic] = useState({ state: 'running', detail: 'Listening…' });
  const [net, setNet] = useState({ state: 'running', detail: 'Checking the connection…' });
  const [error, setError] = useState(null);
  const [level, setLevel] = useState(0);
  const [done, setDone] = useState(false);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);

  const release = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    try { audioCtxRef.current?.close(); } catch { /* already closed */ }
    audioCtxRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const run = useCallback(async () => {
    setError(null);
    setDone(false);
    setCamera({ state: 'running', detail: 'Opening the camera…' });
    setMic({ state: 'running', detail: 'Listening…' });
    setNet({ state: 'running', detail: 'Checking the connection…' });

    // --- connection -----------------------------------------------------
    // Two different things decide whether anyone can watch: a streaming server
    // (which relays and fans out), or failing that a TURN relay (which at least
    // gets through strict networks, one copy per viewer). Neither means the
    // broadcast only reaches people on the same Wi-Fi — worth knowing before
    // the service, not during it.
    const t0 = performance.now();
    try {
      const [capsRes, iceRes] = await Promise.all([
        fetch(`${API_URL}/api/live/capabilities`),
        fetch(`${API_URL}/api/church/live/ice-servers`),
      ]);
      const caps = await capsRes.json();
      const ice = await iceRes.json().catch(() => ({}));
      const ms = Math.round(performance.now() - t0);
      if (caps.livekit) {
        setNet({ state: ms < 1200 ? 'ok' : 'warn',
                 detail: `Streaming server answered in ${ms} ms.` });
      } else if (ice.has_relay) {
        setNet({ state: 'warn',
                 detail: 'No streaming server, so the picture goes straight to '
                         + `each viewer — about ${ice.max_viewers || 6} at once. `
                         + 'A relay is set up, so other networks can connect.' });
      } else {
        setNet({ state: 'warn',
                 detail: 'No streaming server and no relay. Only a handful of '
                         + 'viewers can watch, and people on mobile data or '
                         + 'office networks may not connect at all.' });
      }
    } catch {
      setNet({ state: 'fail', detail: 'Could not reach the server. Check this device is online.' });
    }

    // --- camera ---------------------------------------------------------
    let stream;
    try {
      const got = await requestCameraStream('environment');
      stream = got.stream;
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      const track = stream.getVideoTracks()[0];
      const s = track?.getSettings?.() || {};
      setCamera({ state: 'ok',
                  detail: `${track?.label || 'Camera'} — ${s.width || '?'}×${s.height || '?'}`
                          + (s.frameRate ? ` at ${Math.round(s.frameRate)} fps` : '') });
      if (!got.hasAudio) {
        setMic({ state: 'fail',
                 detail: 'The microphone was refused, so the broadcast would be silent.' });
        setDone(true);
        return;
      }
    } catch (e) {
      setCamera({ state: 'fail', detail: 'The camera would not open.' });
      setMic({ state: 'fail', detail: 'Not tested — the camera failed first.' });
      setError(describeCameraError(e));
      setDone(true);
      return;
    }

    // --- microphone: listen, and show what was actually heard ------------
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      if (ctx.state === 'suspended') await ctx.resume();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const buf = new Float32Array(analyser.fftSize);

      let peak = 0;
      const until = performance.now() + LISTEN_MS;
      await new Promise((resolve) => {
        const tick = () => {
          analyser.getFloatTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i += 1) sum += buf[i] * buf[i];
          const rms = Math.sqrt(sum / buf.length);
          peak = Math.max(peak, rms);
          setLevel(rms);
          if (performance.now() < until) rafRef.current = requestAnimationFrame(tick);
          else resolve();
        };
        tick();
      });
      setMic(peak >= QUIET
        ? { state: 'ok',
            detail: `Heard you clearly (peak ${Math.round(peak * 100)}%).` }
        : { state: 'warn',
            detail: 'Almost nothing was heard. Check the right microphone is '
                    + 'selected and nothing is covering it, then test again.' });
    } catch {
      setMic({ state: 'warn', detail: 'The sound level could not be measured on this device.' });
    }
    setDone(true);
  }, []);

  useEffect(() => {
    if (open) run();
    return () => release();
  }, [open, run, release]);

  const blocked = camera.state === 'fail' || mic.state === 'fail'
    || net.state === 'fail';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) release(); onOpenChange(v); }}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-md"
                     data-testid="preflight-dialog">
        <DialogHeader>
          <DialogTitle>Check camera and sound</DialogTitle>
        </DialogHeader>

        <div className="aspect-video w-full bg-black rounded-lg overflow-hidden">
          <video ref={videoRef} autoPlay playsInline muted
                 className="w-full h-full object-cover"
                 data-testid="preflight-preview" />
        </div>

        <div className="h-1.5 w-full rounded-full bg-slate-800 overflow-hidden"
             title="Microphone level">
          <div className="h-full bg-emerald-400 transition-[width] duration-75"
               style={{ width: `${Math.min(100, Math.round(level * 400))}%` }}
               data-testid="preflight-level" />
        </div>
        {mic.state === 'running' && (
          <p className="text-xs text-slate-400 -mt-1">Say something — "testing, one two".</p>
        )}

        <div>
          <Row icon={Video} label="Camera" {...camera} />
          <Row icon={Mic} label="Microphone" {...mic} />
          <Row icon={Wifi} label="Connection" {...net} />
        </div>

        {error && <CameraTrouble error={error.message} steps={error.steps} />}

        <div className="flex gap-2 pt-1">
          <Button variant="outline" className="flex-1 border-slate-700 bg-transparent"
                  onClick={run} disabled={!done} data-testid="preflight-retest-btn">
            Test again
          </Button>
          <Button className="flex-1 bg-rose-600 hover:bg-rose-500"
                  disabled={!done || blocked}
                  onClick={() => { release(); onOpenChange(false); onPassed?.(); }}
                  data-testid="preflight-continue-btn">
            {blocked ? 'Fix the problem first' : "Looks good"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PreFlightCheck;
